/**
 * Modal to assign a license seat to a user during activation.
 *
 * Extracted from UserManagementPage.tsx (Sub-PR 6.3).
 */

import { X, Loader2, UserCheck } from 'lucide-react'
import type { User } from '../../types'
import type { License } from '../../types/admin'
import { LicenseOptionRadio } from './LicenseOptionRadio'

interface SeatAssignmentModalProps {
  user: User
  licenses: License[]
  selectedLicense: string
  setSelectedLicense: (id: string) => void
  formError: string | null
  formLoading: boolean
  onClose: () => void
  onAssign: () => void
}

function selectAvailableLicenses(licenses: License[], orgId: string | null): License[] {
  return licenses
    .filter(
      (lic) =>
        lic.organisationId === orgId && lic.status === 'active' && lic.seatsUsed < lic.seatsTotal
    )
    .sort((left, right) => {
      if (!left.validUntil && !right.validUntil) return 0
      if (!left.validUntil) return 1
      if (!right.validUntil) return -1
      return new Date(left.validUntil).getTime() - new Date(right.validUntil).getTime()
    })
}

export function SeatAssignmentModal({
  user,
  licenses,
  selectedLicense,
  setSelectedLicense,
  formError,
  formLoading,
  onClose,
  onAssign,
}: SeatAssignmentModalProps): React.ReactElement {
  const availableLicenses = selectAvailableLicenses(licenses, user.organisationId ?? null)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-stone-800">
        <div className="flex items-center justify-between border-b border-stone-200 p-4 dark:border-stone-700">
          <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
            Assign License Seat
          </h2>
          <button
            onClick={onClose}
            className="rounded p-1 hover:bg-stone-100 dark:hover:bg-stone-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <SeatAssignmentBody
          user={user}
          availableLicenses={availableLicenses}
          selectedLicense={selectedLicense}
          setSelectedLicense={setSelectedLicense}
          formError={formError}
          formLoading={formLoading}
          onClose={onClose}
          onAssign={onAssign}
        />
      </div>
    </div>
  )
}

interface SeatAssignmentBodyProps {
  user: User
  availableLicenses: License[]
  selectedLicense: string
  setSelectedLicense: (id: string) => void
  formError: string | null
  formLoading: boolean
  onClose: () => void
  onAssign: () => void
}

function SeatAssignmentBody({
  user,
  availableLicenses,
  selectedLicense,
  setSelectedLicense,
  formError,
  formLoading,
  onClose,
  onAssign,
}: SeatAssignmentBodyProps): React.ReactElement {
  return (
    <div className="space-y-4 p-4">
      <p className="text-sm text-stone-600 dark:text-stone-400">
        To activate <span className="font-medium">{user.displayName ?? user.username}</span>, you
        need to assign them to a license seat.
      </p>
      {formError && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
          {formError}
        </div>
      )}
      <div>
        <label className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">
          Select License
        </label>
        <div className="space-y-2">
          {availableLicenses.map((license) => (
            <LicenseOptionRadio
              key={license.id}
              option={{
                id: license.id,
                licenseKey: license.licenseKey,
                seatsAvailable: license.seatsTotal - license.seatsUsed,
                seatsTotal: license.seatsTotal,
                validUntil: license.validUntil,
              }}
              groupName="license"
              selectedId={selectedLicense}
              onSelect={setSelectedLicense}
            />
          ))}
        </div>
        {availableLicenses.length === 0 && (
          <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
            No available licenses for this organisation. Create a license first or free up seats.
          </p>
        )}
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg px-4 py-2 text-stone-700 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-700"
        >
          Cancel
        </button>
        <button
          onClick={onAssign}
          disabled={formLoading || !selectedLicense}
          className="flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-white hover:bg-[#5c6e94] disabled:opacity-50"
        >
          {formLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <UserCheck className="h-4 w-4" />
          )}
          Assign &amp; Activate
        </button>
      </div>
    </div>
  )
}
